import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Popover from "@mui/material/Popover"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { formatDistanceToNow } from "date-fns"
import { useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import Column, {
  CenterEverythingColumn,
} from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { trimLongString } from "../../../common/utils"
import { GLOBAL_SEARCH_URL_PARAMS } from "../candidate-details/data"
import {
  clearAllNotifications,
  markCandidateImportJobRead,
  markUploadedLeadImportJobRead,
} from "../reducer"
import CandidateAvatar from "./CandidateAvatar"
import {
  NOTIFICATION_TYPES,
  getNotificationItemKey,
  getNotificationPreviewText,
  getNotificationTypeIcon,
  getNotificationTypeLabel,
  isKnownNotificationType,
} from "./notificationTypes"

export default function NotificationsPopover({
  notifications,
  status = "loaded",
  anchorEl,
  onClose,
}) {
  const dispatch = useDispatch()
  const [isClearing, setIsClearing] = useState(false)
  const isInitialLoading = status === "loading" && notifications.length === 0
  const isError = status === "error" && notifications.length === 0
  const canClear = notifications.length > 0 && !isInitialLoading && !isError

  function handleClearAll() {
    if (!canClear || isClearing) {
      return
    }
    setIsClearing(true)
    const result = dispatch(clearAllNotifications())
    const finished =
      typeof result?.unwrap === "function" ? result.unwrap() : Promise.resolve()
    Promise.resolve(finished)
      .catch(() => {})
      .finally(() => {
        setIsClearing(false)
      })
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      elevation={1}
      anchorOrigin={{
        horizontal: "center",
        vertical: "center",
      }}
      transformOrigin={{
        horizontal: "right",
        vertical: "top",
      }}
      sx={{
        "& .MuiPaper-root": {
          width: "400px",
        },
      }}
    >
      <Column
        sx={{
          position: "relative",
          minHeight: "200px",
          maxHeight: "450px",
        }}
      >
        <HeaderRow>
          <Row sx={{ alignItems: "center", gap: 1 }}>
            <Typography variant="h5" color="text.secondary">
              Notifications
            </Typography>
            <Chip color="primary" label={notifications.length} size="small" />
          </Row>
          <Row sx={{ alignItems: "center", gap: 0.5 }}>
            {canClear ? (
              <Button
                size="small"
                onClick={handleClearAll}
                disabled={isClearing}
                aria-label="Clear all notifications"
              >
                {isClearing ? "Clearing…" : "Clear all"}
              </Button>
            ) : null}
            <IconButton onClick={onClose} aria-label="Close notifications">
              <CloseOutlinedIcon />
            </IconButton>
          </Row>
        </HeaderRow>
        {isInitialLoading ? (
          <CenterEverythingColumn>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading notifications…
            </Typography>
          </CenterEverythingColumn>
        ) : isError ? (
          <CenterEverythingColumn>
            <Typography variant="h6" color="text.secondary">
              Could not load notifications.
            </Typography>
          </CenterEverythingColumn>
        ) : notifications.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="h6" color="text.secondary">
              No unread notifications.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          <Box sx={{ overflowY: "auto" }}>
            {notifications.map((notification, i) => (
              <Box key={getNotificationItemKey(notification, i)}>
                <NotificationItem
                  notification={notification}
                  onClose={onClose}
                />
                {i < notifications.length - 1 ? <Divider /> : null}
              </Box>
            ))}
          </Box>
        )}
      </Column>
    </Popover>
  )
}

function NotificationItem({ notification, onClose }) {
  switch (notification.type) {
    case NOTIFICATION_TYPES.email:
      return <EmailNotification notification={notification} onClose={onClose} />
    case NOTIFICATION_TYPES.sms:
      return <SMSNotifcation notification={notification} onClose={onClose} />
    case NOTIFICATION_TYPES.candidateImport:
      return (
        <CandidateImportNotification
          notification={notification}
          onClose={onClose}
        />
      )
    case NOTIFICATION_TYPES.uploadedLeadImport:
      return (
        <UploadedLeadImportNotification
          notification={notification}
          onClose={onClose}
        />
      )
    case NOTIFICATION_TYPES.conciergeInboxTemplatePending:
      return (
        <ConciergeInboxTemplatePendingNotification
          notification={notification}
          onClose={onClose}
        />
      )
    default:
      return (
        <UnknownNotification notification={notification} onClose={onClose} />
      )
  }
}

function EmailNotification({ notification, onClose }) {
  const navigate = useNavigate()
  const candidate = notification?.event?.candidate
  if (!candidate) {
    return <UnknownNotification notification={notification} onClose={onClose} />
  }

  function handleNotificationClicked() {
    navigate(
      `/candidates/${candidate.id}?${GLOBAL_SEARCH_URL_PARAMS.emailTransaction}=${notification.id}`
    )
  }

  return (
    <BaseNotification
      type={NOTIFICATION_TYPES.email}
      candidate={candidate}
      eventDate={notification.date}
      eventName="email received"
      eventText={notification.subject}
      onClick={handleNotificationClicked}
      onClose={onClose}
    />
  )
}

function SMSNotifcation({ notification, onClose }) {
  const navigate = useNavigate()
  const candidate = notification?.send_event?.candidate
  if (!candidate) {
    return <UnknownNotification notification={notification} onClose={onClose} />
  }

  function handleNotificationClicked() {
    navigate(
      `/candidates/${candidate.id}?${GLOBAL_SEARCH_URL_PARAMS.sms}=${notification.id}`
    )
  }

  return (
    <BaseNotification
      type={NOTIFICATION_TYPES.sms}
      candidate={candidate}
      eventDate={notification.created}
      eventName="sms received"
      eventText={notification.text}
      onClick={handleNotificationClicked}
      onClose={onClose}
    />
  )
}

function CandidateImportNotification({ notification, onClose }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  function handleNotificationClicked() {
    onClose()
    dispatch(markCandidateImportJobRead(notification))
    navigate("/candidates")
  }

  return (
    <JobNotification
      notification={notification}
      title="Candidate import"
      onClick={handleNotificationClicked}
    />
  )
}

function UploadedLeadImportNotification({ notification, onClose }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  function handleNotificationClicked() {
    onClose()
    dispatch(markUploadedLeadImportJobRead(notification))
    navigate("/leads/?tab=uploaded")
  }

  return (
    <JobNotification
      notification={notification}
      title="Uploaded lead import"
      onClick={handleNotificationClicked}
    />
  )
}

function ConciergeInboxTemplatePendingNotification({ notification, onClose }) {
  const navigate = useNavigate()
  const mediumLabel = notification.medium === "sms" ? "SMS" : "Email"

  function handleNotificationClicked() {
    onClose()
    navigate("/settings?tab=concierge")
  }

  return (
    <NotificationContainer onClick={handleNotificationClicked}>
      <NotificationTypeIcon type={notification.type} />
      <Column sx={{ width: "100%", gap: 1, minWidth: 0 }}>
        <Row
          sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            Concierge {mediumLabel} template
          </Typography>
          <RelativeTime value={notification.created} />
        </Row>
        <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
          {trimLongString(
            notification.message ||
              `“${notification.name}” is waiting for your approval.`,
            80
          )}
        </Typography>
      </Column>
    </NotificationContainer>
  )
}

function JobNotification({ notification, title, onClick }) {
  return (
    <NotificationContainer onClick={onClick}>
      <NotificationTypeIcon type={notification.type} />
      <Column sx={{ width: "100%", gap: 1, minWidth: 0 }}>
        <Row
          sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            {title}
          </Typography>
          <RelativeTime
            value={notification.completed_at || notification.created}
          />
        </Row>
        <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
          {trimLongString(notification.message, 80)}
        </Typography>
      </Column>
    </NotificationContainer>
  )
}

function UnknownNotification({ notification, onClose }) {
  return (
    <NotificationContainer onClick={onClose}>
      <NotificationTypeIcon type={notification.type} />
      <Column sx={{ width: "100%", gap: 1, minWidth: 0 }}>
        <Row
          sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            {isKnownNotificationType(notification.type)
              ? getNotificationTypeLabel(notification.type)
              : "Notification"}
          </Typography>
          <RelativeTime
            value={
              notification.completed_at ||
              notification.date ||
              notification.created
            }
          />
        </Row>
        <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
          {trimLongString(getNotificationPreviewText(notification), 80)}
        </Typography>
      </Column>
    </NotificationContainer>
  )
}

function BaseNotification({
  type,
  candidate,
  eventName,
  eventDate,
  eventText,
  onClose,
  onClick,
}) {
  const TypeIcon = getNotificationTypeIcon(type)

  function handleNotificationClicked() {
    onClose()
    onClick()
  }

  return (
    <NotificationContainer onClick={handleNotificationClicked}>
      <Row sx={{ position: "relative", alignSelf: "center" }}>
        <CandidateAvatar candidate={candidate} size="40px" fontSize="1.1em" />
        <TypeIconBadge aria-hidden={false}>
          <TypeIcon
            sx={{ fontSize: 12 }}
            aria-label={`${getNotificationTypeLabel(type)} notification`}
          />
        </TypeIconBadge>
      </Row>
      <Column sx={{ width: "100%", gap: 1, minWidth: 0 }}>
        <Row
          sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}
        >
          <Row sx={{ alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "16px" }}
              noWrap
              title={candidate.candidate_name}
            >
              {candidate.candidate_name}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.8em" }} noWrap>
              {eventName}
            </Typography>
          </Row>
          <RelativeTime value={eventDate} />
        </Row>
        <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
          {trimLongString(eventText, 40)}
        </Typography>
      </Column>
    </NotificationContainer>
  )
}

function NotificationTypeIcon({ type }) {
  const Icon = getNotificationTypeIcon(type)
  return (
    <TypeIconWrap>
      <Icon
        fontSize="small"
        color="action"
        aria-label={`${getNotificationTypeLabel(type)} notification`}
      />
    </TypeIconWrap>
  )
}

function RelativeTime({ value }) {
  const label = formatRelativeTime(value)
  if (!label) {
    return null
  }
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontSize: "0.8em", flexShrink: 0 }}
    >
      {label}
    </Typography>
  )
}

function formatRelativeTime(value) {
  if (!value) {
    return null
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return formatDistanceToNow(date, { addSuffix: true })
}

const HeaderRow = styled(Row)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(1),
  position: "sticky",
  top: 0,
  zIndex: 10000,
  backgroundColor: "white",
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const NotificationContainer = styled(Row)(({ theme }) => ({
  padding: theme.spacing(2),
  gap: theme.spacing(1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor:
      theme.palette.colors?.lightBackground || theme.palette.action.hover,
  },
}))

const TypeIconWrap = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  alignSelf: "center",
  backgroundColor: theme.palette.action.hover,
}))

const TypeIconBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: -4,
  bottom: -4,
  width: 20,
  height: 20,
  borderRadius: "50%",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[1],
}))
